<template>
  <div class="make-action">
    <v-form
      ref="makeActionForm"
      v-model="isFormValid"
      @submit.prevent="submitForm"
    >
      <label class="input-label mb-2 ml-2">Działanie</label>
      <v-select
        v-model="selectedAction"
        v-bind="props.select"
        :rules="[rules.objectEmpty]"
        :hide-details="false"
        :items="actions"
        item-text="label"
        return-object
        :readonly="specificData && !!specificData.action"
        height="40"
        placeholder="Wybierz działanie"
        @change="clearComment"
      />

      <v-expand-transition>
        <div v-if="selectedAction.userRequired">
          <label class="input-label mb-2 ml-2">Wybierz Użytkownika</label>
          <v-autocomplete
            v-model="selectedUser"
            v-bind="props.select"
            :rules="[rules.required]"
            :hide-details="false"
            :items="availableUsersList"
            item-value="id"
            :item-text="(item) => getUserName(item)"
            height="40"
            placeholder="Wybierz użytkownika"
          >
            <template #item="{ item }">
              {{ getUserName(item) }}
              <v-spacer />
              <span
                v-if="item.replaced"
                class="error--text"
              >(nieobecny)</span>
            </template>
          </v-autocomplete>
        </div>
      </v-expand-transition>

      <v-expand-transition>
        <div v-if="selectedAction.fileRequired">
          <label class="input-label mb-5 ml-2">
            Pliki finalne
            <span
              v-if="!fileIds.length && !youtubeFinal"
              class="red--text font-weight-bold text-capitalize"
            >
              Wymagane
            </span>
          </label>
          <AppFilesTable
            v-model="fileIds"
            style="border: 1px solid #dde3e3"
            :files="acceptanceFiles"
            :columns="['select', 'filename', 'size']"
            class="mb-5"
          />
          <div
            v-if="project.youtubeLink"
            class="d-flex align-center mb-2"
          >
            <v-checkbox
              v-model="youtubeFinal"
              dense
              hide-details
              class="mt-0 pa-0"
            /><label class="text--body-2">Oznacz plik youtube jako finalny</label>
          </div>
        </div>
      </v-expand-transition>

      <label class="input-label mb-2 ml-2">Komentarz</label>
      <v-textarea
        v-model="comment"
        v-bind="props.textField"
        :readonly="!selectedAction.commentable"
        :placeholder="!selectedAction.commentable ? 'Musisz wybrać działanie by dodać komentarz.' : ''"
        no-resize
        :rules="selectedAction.commentRequired ? [rules.required] : []"
        height="120"
      />

      <v-expand-transition>
        <div
          v-if="graphicRevision && selectedAction.name === 'accept'"
          class="caption make-action__revision"
        >
          <p class="mb-1">
            Pracownik („<b>Twórca</b>” lub „<b>Współtwórca</b>”) oświadcza, że:
          </p>
          <ol>
            <li class="mb-1">
              przyjmuje do realizacji utwór pracowniczy w rozumieniu art. 12 ustawy Prawo autorskie i prawa
              pokrewne(dalej jako „*Utwór*”) i jest świadomy, że przyjęty do realizacji Utwór stworzy w wyniku
              wykonywania obowiązków ze stosunku pracy. Oznacza to, że z chwilą przyjęcia Utworu przez pracodawcę,
              pracodawca nabędzie własność autorskie prawa majątkowe do zrealizowanego Utworu oraz do przedmiotu, na
              którym Utwór utrwalono;
            </li>
            <li class="mb-1">
              przyjęty do realizacji Utwór zostanie przez niego zrealizowany zgodnie z Opisem projektu, określonym w
              Metryce;
            </li>
            <li class="mb-1">
              będą mu przysługiwały autorskie prawa majątkowe do zrealizowanego Utworu, a w przypadku wykorzystania w
              Utworze utworów cudzych lub posiadania majątkowych praw autorskich wspólnie z innymi podmiotami, że jest
              uprawnionym do rozporządzania nimi w sposób,który umożliwi wykorzystanie przez NBP Utworu zgodnie z Opisem
              projektu, określonym w Metryce;
            </li>
            <li class="mb-1">
              jest świadomy obowiązku uzupełnienia Metryki Utworu o aktualne informacje dotyczące autorstwa Utworu przed
              przystąpieniem do przekazania Utworu pracodawcy;
            </li>
            <li>
              w chwili przyjmowania Utworu przez pracodawcę, Utwór będzie wolny od jakichkolwiek wad prawnych i
              fizycznych.
            </li>
          </ol>
        </div>
      </v-expand-transition>

      <v-btn
        class="custom-submit-button mt-2"
        color="primary"
        type="submit"
        :loading="isActionProcessing"
      >
        Wyślij
      </v-btn>
    </v-form>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import props from '@/helpers/componentsProps';
import rules from '@/helpers/validators';
import utils from '@/helpers/utils';
import AppMultiselectChoices from '@/components/UI/AppMultiselectChoices.vue';
import AppFilesTable from '@/components/UI/AppFilesTable.vue';

export default {
  props: {
    id: {
      type: [Number, String],
      required: true,
    },
    actions: {
      type: Array,
      required: true,
    },
    kiw: {
      type: Boolean,
      default: false,
    },
    commercialPrint: {
      type: Boolean,
      default: false,
    },
    files: {
      type: Array,
      default: () => [],
    },
    graphicRevision: {
      type: Boolean,
      default: false,
    },
    specificData: {
      type: Object,
      default: null,
    },
  },

  components: {
    AppMultiselectChoices,
    AppFilesTable,
  },

  data() {
    return {
      props,
      rules,
      isFormValid: true,
      selectedAction: {},
      comment: null,
      selectedUser: '',
      fileIds: [],
      youtubeFinal: undefined,
    };
  },

  computed: {
    ...mapState(['isActionProcessing']),
    ...mapState('users', ['usersSimplifiedList']),
    ...mapState('filters', ['filtersOptions']),
    ...mapState('auth', ['user']),
    ...mapState('project', ['project']),
    ...mapState('commercialPrintSingle', {
      commercialPrintData: 'commercialPrint'
    }),

    acceptanceFiles() {
      return this.files.filter((file) => file.currentStatus === 'accepted');
    },

    isCoordinatorRequired() {
      const statusesRequiringCoordinator = [
        'send_to_print_coordinator_review',
        'unarchive_and_send_to_print_coordinator_review',
      ];
      return this.selectedAction.userRequired && statusesRequiringCoordinator.includes(this.selectedAction.name);
    },

    availableUsersList() {
      return (
        this.usersSimplifiedList.filter(
          (user) => this.project?.members?.every((member) => member.userId !== user.id) && user.id !== this.user.id
        ) || []
      );
    },
  },

  watch: {
    isCoordinatorRequired(isRequired) {
      let filters;
      if (isRequired) {
        const coordinatorRoleId = this.filtersOptions.roles.find((role) => role.name === 'print_coordinator')?.id;
        filters = { roleIds: [coordinatorRoleId] };
      }
      this.fetchSimplifiedUsersList(filters);
    },

    'selectedAction.fileRequired'(isRequired) {
      if (isRequired) {
        this.fileIds = this.acceptanceFiles.map((file) => file.id);
      } else {
        this.fileIds = [];
      }
    },
  },

  created() {
    this.fetchSimplifiedUsersList();
    if (this.specificData) {
      this.fetchProject(this.id);
      this.selectedAction = this.specificData.action;
      this.fileIds = this.specificData.fileIds;
    }
  },

  methods: {
    ...mapActions('project', ['makeProjectAction', 'fetchProject']),
    ...mapActions('quickAcceptance', ['fetchProjects']),
    ...mapActions('kiwSingle', ['makeKIWAction']),
    ...mapActions('commercialPrintSingle', ['makeCommercialPrintAction']),
    ...mapActions('users', ['fetchSimplifiedUsersList']),

    getUserName(user) {
      return utils.getFullName(user);
    },

    clearValidation() {
      this.$refs.makeActionForm.resetValidation();
    },

    clearComment() {
      this.comment = null;
      this.clearValidation();
    },

    submitForm() {
      this.$refs.makeActionForm.validate();

      if (this.isFormValid) {
        const actionData = {
          actionName: this.selectedAction.name,
          comment: this.comment,
          oldStatus: this.project.status,
        };
        if (this.kiw) {
          this.makeKIWAction(actionData);
        } else if (this.commercialPrint) {
          this.makeCommercialPrintAction({ ...actionData, oldStatus: this.commercialPrintData.status });
        } else {
          if (this.isCoordinatorRequired) {
            actionData.coordinatorUserId = this.selectedUser;
          } else {
            actionData.verificationUserId = this.selectedUser;
          }
          if (this.fileIds.length) actionData.fileIds = this.fileIds;
          if (this.youtubeFinal) actionData.youtubeFinal = this.youtubeFinal;
          this.makeProjectAction(actionData).then(() => {
            if (this.specificData) this.fetchProjects();
            const { params } = this.$route; // { id }
            if (actionData.actionName === 'unarchive') {
              this.$router.replace({ name: 'project-single', params });
            }
          });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.make-action::v-deep textarea {
  padding: 4px !important;
}
.make-action__revision {
  margin: 0 0 16px 0;
}
</style>
